import {
    useRef,
    useState
} from 'react';

import {SimpleGrid} from '@mantine/core';

import {modals} from '@mantine/modals';
import {Text} from '@mantine/core';

import {useMeters} from '../hooks/useMeters.ts';
import {useMetersSelection} from '../hooks/useMetersSelection.ts';
import {useTableColumns} from '../hooks/useTableColumns.tsx';
import {FormDrawer} from '../drawers/FormDrawer.tsx';
import {setStatus, update} from '../services/MetersService.ts';
import {buildUpdateMeterInput} from '../services/buildMeterInput.ts';

import {
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {DataTableTokenPagination} from "../../../components/DataTableTokenPagination.tsx";
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';
import {Meter} from '../types';
import {useQueryClient} from '@tanstack/react-query';

export const RecordsTable = () => {
    const {selectedRecords, setSelectedRecords} = useMetersSelection();
    const {columns} = useTableColumns();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<Meter | null>(null);
    const queryClient = useQueryClient();

    const {
        items,
        totalCount,
        loading,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useMeters();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // FNP-768: apply the edit, routing a status change through setMeterStatus (billing-api owns
    // the transition lattice + guards). FIELDS FIRST (updateMeter, via the whitelisted
    // buildUpdateMeterInput — no more blacklist spread), THEN the TRANSITION — so a rejected
    // transition never leaves a de-provisioned meter behind applied edits.
    const applyEdit = async (record: Meter, values: Meter) => {
        try {
            await update(record.id as string, buildUpdateMeterInput(values) as Meter);
            const statusChanged = values.status !== record.status;
            if (statusChanged) {
                await setStatus(record.id as string, values.status as unknown as string);
            }
            queryClient.invalidateQueries({queryKey: ['meters']});
            notifySuccess('Meter updated');
            setDrawerOpened(false);
            setSelectedRecord(null);
        } catch (e) {
            // Surfaces MeterHasOpenPeriods / IllegalMeterStatusTransition from setMeterStatus.
            notifyError(e);
        }
    };

    const handleDrawerSubmit = async (values: Meter) => {
        const record = selectedRecord;
        if (!record?.id) {
            return;
        }

        // FNP-768 (architect caveat): RETIRE de-provisions the live Stripe meter via the FNP-589
        // reconciler — a money-path teardown. A plain Save must NOT silently trigger it, so a
        // transition TO RETIRED is gated behind an explicit confirmation naming the consequence.
        const retiring = values.status === 'RETIRED' && record.status !== 'RETIRED';
        if (retiring) {
            modals.openConfirmModal({
                title: 'Retire this meter?',
                children: (
                    <Text size="sm">
                        Retiring <b>{record.name}</b> ({record.meterCode}) de-provisions its Stripe
                        billing meter and is terminal. It is blocked if the meter still has open
                        metering periods. Continue?
                    </Text>
                ),
                labels: {confirm: 'Retire meter', cancel: 'Cancel'},
                confirmProps: {color: 'red'},
                onConfirm: () => applyEdit(record, values),
            });
            return;
        }

        await applyEdit(record, values);
    };

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            <MantineDataTable
                columns={columns}
                records={items}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={({record}) => {
                    setSelectedRecord(record);
                    setDrawerOpened(true);
                }}
            />

            <DataTableTokenPagination totalCount={totalCount.toString()} nextToken={nextToken}
                                      handleLoadMore={fetchRecords}/>

            {drawerOpened && selectedRecord && (
                <FormDrawer
                    record={selectedRecord}
                    handleSubmit={handleDrawerSubmit}
                    opened={drawerOpened}
                    onClose={() => {
                        setDrawerOpened(false);
                        setSelectedRecord(null);
                    }}
                />
            )}
        </SimpleGrid>
    );
};
